import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

import { FitDataService, FtpPendingStatus } from '../../training-analysis/fit-data.service';

/**
 * Globales Banner für Trainingseinheiten ohne FTP.
 *
 * Erscheint oben auf der Seite, sobald der aktuelle Sportler `ftp_pending`-
 * Sessions hat. Liegen genug Daten vor (>= 90 Tage Historie + CP-Fit), kann
 * der User die Berechnung per Button anstoßen; sonst zeigt das Banner nur an,
 * dass automatisch nachberechnet wird, sobald genug Daten vorliegen.
 */
@Component({
  selector: 'app-ftp-pending-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  template: `
    @if (visible) {
      <div class="fp-banner" [class.fp-banner--running]="triggering">
        <mat-icon class="fp-icon">{{ triggering ? 'hourglass_top' : 'bolt' }}</mat-icon>

        <div class="fp-text">
          @if (triggering) {
            <strong>{{ 'calendar.ftp_banner.running_title' | translate }}</strong>
            <span class="fp-body">
              {{ 'calendar.ftp_banner.running_body' | translate:{ n: status?.pending_count } }}
            </span>
          } @else if (status?.can_resolve) {
            <strong>
              {{ 'calendar.ftp_banner.ready_title' | translate:{ n: status?.pending_count } }}
            </strong>
            <span class="fp-body">{{ 'calendar.ftp_banner.ready_body' | translate }}</span>
          } @else {
            <strong>
              {{ 'calendar.ftp_banner.waiting_title' | translate:{ n: status?.pending_count } }}
            </strong>
            <span class="fp-body">
              {{ 'calendar.ftp_banner.waiting_body' | translate:{ d: status?.min_history_days } }}
            </span>
          }
          @if (failed) {
            <span class="fp-error">{{ 'calendar.ftp_banner.failed' | translate }}</span>
          }
        </div>

        <span class="grow"></span>

        @if (triggering) {
          <mat-spinner diameter="20"></mat-spinner>
        } @else if (status?.can_resolve) {
          <button mat-flat-button color="primary" (click)="resolve()">
            {{ 'calendar.ftp_banner.cta' | translate }}
          </button>
        }
        <button mat-icon-button (click)="dismiss()" aria-label="dismiss" [disabled]="triggering">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    }
  `,
  styles: [`
    .fp-banner {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      padding: 0.6rem 0.85rem;
      margin: 0.5rem 0;
      background: #e3f2fd;
      color: #0d47a1;
      border-left: 4px solid #1976d2;
      border-radius: 6px;
      font-size: var(--fs-sm);
    }
    .fp-banner--running { background: #eef2f7; color: #37474f; border-left-color: #607d8b; }
    .fp-icon { color: #1976d2; flex-shrink: 0; margin-top: 0.1rem; }
    .fp-banner--running .fp-icon { color: #607d8b; }
    .fp-text { flex: 1; min-width: 0; }
    .fp-text strong { display: block; }
    .fp-body { display: block; margin-top: 0.15rem; color: inherit; }
    .fp-error { display: block; margin-top: 0.2rem; color: var(--color-warn, #d97706); }
    .grow { flex: 1; }
  `],
})
export class FtpPendingBannerComponent implements OnInit, OnDestroy {
  private readonly fitData = inject(FitDataService);
  private readonly cdr = inject(ChangeDetectorRef);

  status: FtpPendingStatus | null = null;
  triggering = false;
  failed = false;
  private suppressed = false;
  private pollSub?: Subscription;

  get visible(): boolean {
    return !this.suppressed && !!this.status && this.status.pending_count > 0;
  }

  ngOnInit(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  private refresh(): void {
    this.fitData.getFtpPendingStatus().subscribe({
      next: (s) => {
        this.status = s;
        if (s?.running) {
          this.startPolling();
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.status = null;
        this.cdr.markForCheck();
      },
    });
  }

  resolve(): void {
    if (this.triggering || !this.status?.can_resolve) {
      return;
    }
    this.triggering = true;
    this.failed = false;
    this.cdr.markForCheck();
    this.fitData.triggerFtpResolve().subscribe({
      next: () => this.startPolling(),
      error: (err) => {
        // 409 = a resolve job is already running -> just poll its progress.
        if (err?.status === 409) {
          this.startPolling();
        } else {
          this.triggering = false;
          this.failed = true;
          this.cdr.markForCheck();
        }
      },
    });
  }

  private startPolling(): void {
    this.triggering = true;
    this.cdr.markForCheck();
    this.pollSub?.unsubscribe();
    this.pollSub = interval(8000)
      .pipe(
        switchMap(() => this.fitData.getFtpPendingStatus()),
        // keep emitting while a job runs; emit the final (idle) state too.
        takeWhile((s) => !!s && s.running, true),
      )
      .subscribe({
        next: (s) => {
          this.status = s;
          if (s && !s.running) {
            this.triggering = false;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.triggering = false;
          this.cdr.markForCheck();
        },
      });
  }

  dismiss(): void {
    this.suppressed = true;
    this.pollSub?.unsubscribe();
    this.cdr.markForCheck();
  }
}
